/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import FormData from 'form-data';
import { QueryClient } from '@tanstack/react-query';

// import errors can be ignored as this file will be copied to different location
import { ApiError } from '../../api/core/ApiError';
import type { ApiRequestOptions } from '../../api/core/ApiRequestOptions';
import type { ApiResult } from '../../api/core/ApiResult';
import type { OnCancel } from '../../api/core/CancelablePromise';
import { CancelablePromise } from '../../api/core/CancelablePromise';
import type { OpenAPIConfig } from '../../api/core/OpenAPI';
import type { UserDetail } from '../../api/models/UserDetail';
import { isAnonymousTolerantPath } from '../../utils/anonymousRoutes';
import { getCookieValue } from '../../utils/cookies';
import { setSessionExpiryInProgress } from '../../app/components/UnsavedChangesFormPrompt';
import { readActingAsEntity } from '../../../v2/js/utils/actingAsEntity';

// Context headers function
const getContextHeaders = (queryClient: QueryClient) => {
  const user = queryClient.getQueryData<UserDetail>(['user', 'detail', 'current']);

  if (!user) return {};

  let contextType: string = '';
  let contextId: string = '';
  let contextMemberId: string = '';
  if (user.user_type == 'provider') {
    contextType = 'provider_id';
    contextId = user.provider_id;
    contextMemberId = user.provider_id;
  } else if (user.user_type == 'healthcare_entity_rep') {
    contextType = 'healthcare_entity_id';
    contextId = user.healthcare_entity_id;
    contextMemberId = user.healthcare_entity_rep_id;
  } else if (user.user_type == 'staff') {
    const actingAs = readActingAsEntity();
    contextType = 'user_id';
    contextId = actingAs?.id || user.id;
    contextMemberId = user.id;
  }

  return {
    'SIH-Context-Type': contextType,
    'SIH-Context-ID': contextId,
    'SIH-Context-Member-ID': contextMemberId,
  };
};

let queryClientInstance: QueryClient | null = null;

export const setQueryClientInstance = (client: QueryClient) => {
  queryClientInstance = client;
};

export const api = axios.create({
  baseURL: window.APPLICATION_HOST,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface SessionExpiredHandler {
  handleSessionExpired: (isAuthError?: boolean) => void;
}

/**
 * Last time an API request completed successfully, shared across tabs.
 * Any authenticated request also rolls the server-side session
 * (SESSION_SAVE_EVERY_REQUEST), so this timestamp is the client-side mirror
 * of the idle clock — SessionExpiryWarning uses it to warn before logout.
 */
export const SESSION_LAST_ACTIVITY_STORAGE_KEY = 'sessionLastActivityAt';
const ACTIVITY_MARK_THROTTLE_MS = 5000;
let lastActivityMarkAt = 0;

export const markSessionActivity = () => {
  const now = Date.now();
  if (now - lastActivityMarkAt < ACTIVITY_MARK_THROTTLE_MS) return;
  lastActivityMarkAt = now;
  try {
    localStorage.setItem(SESSION_LAST_ACTIVITY_STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable (private mode/quota) — the warning falls back to
    // its mount time and still fails safe.
  }
};

let sessionHandler: SessionExpiredHandler | null = null;
// Debounced guard (not a one-shot latch): a burst of parallel 403s must fire
// the expiry handler only once, but the guard has to self-heal. A permanent
// latch wedged the whole tab whenever it was burned by a code path that never
// navigated away — every later auth error was then silently eaten, so an
// expired session rendered as empty lists instead of a login redirect.
let sessionExpiredHandledAt = 0;
const SESSION_EXPIRED_DEBOUNCE_MS = 10000;

export const setSessionHandler = (handler: SessionExpiredHandler) => {
  sessionHandler = handler;
  // Reset the guard when a new handler is registered (e.g., after a reload)
  sessionExpiredHandledAt = 0;
};

api.interceptors.request.use((config) => {
  const csrftoken = getCookieValue('csrftoken');
  if (csrftoken) {
    config.headers['X-CSRFTOKEN'] = csrftoken;
  }
  if (queryClientInstance) {
    const contextHeaders = getContextHeaders(queryClientInstance);

    config.headers = {
      ...config.headers,
      ...contextHeaders,
    } as AxiosHeaders;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    markSessionActivity();
    return response;
  },
  (error) => {
    // Any response — success or failure — means the server processed a request
    // and (with SESSION_SAVE_EVERY_REQUEST) rolled the session, so stamp
    // activity for non-auth errors too or the idle prediction runs ahead of
    // the server clock. Auth errors are the exception: an expired/anonymous
    // session is not rolled. No response (network failure) proves nothing.
    const status = error.response?.status;
    if (status && status !== 401 && status !== 403) {
      markSessionActivity();
    }

    // Handle authentication errors
    if (status === 401 || status === 403) {
      // Public-by-design and session-establishment routes accept an
      // anonymous state — a 403 from a query fired on these pages is not
      // an "auth expired" event and must not bounce the user to /login.
      // Session.tsx already gates its own user-fetch via the same helper;
      // mirroring that gate here keeps the two in lockstep.
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      if (isAnonymousTolerantPath(currentPath)) {
        return Promise.reject(error);
      }

      // The login page intentionally probes the current-user endpoint to detect
      // an existing session (Session.tsx), so an auth 403 there is expected —
      // it must not fire the expiry handler nor consume the debounce window.
      // /login is NOT in isAnonymousTolerantPath because Session.tsx uses that
      // helper to skip the probe entirely, which the login page must not do.
      if (currentPath.startsWith('/login')) {
        return Promise.reject(error);
      }

      // Safety net: if the csrftoken cookie is gone, treat any 401/403 as an
      // auth-expired event regardless of the response detail. Some permission
      // classes return a generic "You do not have permission" 403 when the user
      // is anonymous, which doesn't match the specific detail strings below.
      // Note: we cannot read `sessionid` from JS (SESSION_COOKIE_HTTPONLY=True);
      // csrftoken is the JS-visible proxy and is cleared alongside the session.
      const sessionCookieGone = !getCookieValue('csrftoken');

      const isAuthenticationError =
        error.response?.data?.detail === 'Authentication credentials were not provided.' ||
        error.response?.data?.detail === 'Invalid token.' ||
        error.response?.data?.detail === 'Session has expired.' ||
        error.response?.status === 401 ||
        sessionCookieGone;

      if (
        isAuthenticationError &&
        sessionHandler &&
        Date.now() - sessionExpiredHandledAt > SESSION_EXPIRED_DEBOUNCE_MS
      ) {
        sessionExpiredHandledAt = Date.now();
        setSessionExpiryInProgress(true);
        sessionHandler.handleSessionExpired(true);
      }
    }

    return Promise.reject(error);
  }
);

export function isDefined<T>(value: T | null | undefined): value is Exclude<T, null | undefined> {
  return value !== undefined && value !== null;
}

export const isString = (value: any): value is string => {
  return typeof value === 'string';
};

export const isStringWithValue = (value: any): value is string => {
  return isString(value) && value !== '';
};

export const isBlob = (value: any): value is Blob => {
  return (
    typeof value === 'object' &&
    typeof value.type === 'string' &&
    typeof value.stream === 'function' &&
    typeof value.arrayBuffer === 'function' &&
    typeof value.constructor === 'function' &&
    typeof value.constructor.name === 'string' &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

export const isFormData = (value: any): value is FormData => {
  return value instanceof FormData;
};

export const isSuccess = (status: number): boolean => {
  return status >= 200 && status < 300;
};

export const base64 = (str: string): string => {
  try {
    return btoa(str);
  } catch (err) {
    // @ts-ignore
    return Buffer.from(str).toString('base64');
  }
};

export const getQueryString = (params: Record<string, any>): string => {
  const qs: string[] = [];

  const append = (key: string, value: any) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          process(key, v);
        });
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          process(`${key}[${k}]`, v);
        });
      } else {
        append(key, value);
      }
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  if (qs.length > 0) {
    return `?${qs.join('&')}`;
  }

  return '';
};

const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
  const encoder = config.ENCODE_PATH || encodeURI;

  const path = options.url
    .replace('{api-version}', config.VERSION)
    .replace(/{(.*?)}/g, (substring: string, group: string) => {
      if (options.path?.hasOwnProperty(group)) {
        return encoder(String(options.path[group]));
      }
      return substring;
    });

  const url = `${config.BASE}${path}`;
  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

export const getFormData = (options: ApiRequestOptions): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: any) => {
      if (isString(value) || isBlob(value)) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    };

    Object.entries(options.formData)
      .filter(([_, value]) => isDefined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => process(key, v));
        } else {
          process(key, value);
        }
      });

    return formData;
  }
  return undefined;
};

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

export async function resolve<T>(
  options: ApiRequestOptions,
  resolver?: T | Resolver<T>
): Promise<T | undefined> {
  if (typeof resolver === 'function') {
    return (resolver as Resolver<T>)(options);
  }
  return resolver;
}

export const getHeaders = async (
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  formData?: FormData
): Promise<Record<string, string>> => {
  const token = await resolve(options, config.TOKEN);
  const username = await resolve(options, config.USERNAME);
  const password = await resolve(options, config.PASSWORD);
  const additionalHeaders = await resolve(options, config.HEADERS);
  const formHeaders = (typeof formData?.getHeaders === 'function' && formData?.getHeaders()) || {};

  const headers = Object.entries({
    Accept: 'application/json',
    ...additionalHeaders,
    ...options.headers,
    ...formHeaders,
  })
    .filter(([_, value]) => isDefined(value))
    .reduce(
      (headers, [key, value]) => ({
        ...headers,
        [key]: String(value),
      }),
      {} as Record<string, string>
    );

  if (isStringWithValue(token)) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (isStringWithValue(username) && isStringWithValue(password)) {
    const credentials = base64(`${username}:${password}`);
    headers['Authorization'] = `Basic ${credentials}`;
  }

  if (options.body) {
    if (options.mediaType) {
      headers['Content-Type'] = options.mediaType;
    } else if (isBlob(options.body)) {
      headers['Content-Type'] = options.body.type || 'application/octet-stream';
    } else if (isString(options.body)) {
      headers['Content-Type'] = 'text/plain';
    } else if (!isFormData(options.body)) {
      headers['Content-Type'] = 'application/json';
    }
  }

  return headers;
};

export const getRequestBody = (options: ApiRequestOptions): any => {
  if (options.body) {
    return options.body;
  }
  return undefined;
};

export async function sendRequest<T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance
): Promise<AxiosResponse<T>> {
  const source = axios.CancelToken.source();

  const requestConfig: AxiosRequestConfig = {
    url,
    headers,
    data: body ?? formData,
    method: options.method,
    withCredentials: config.WITH_CREDENTIALS,
    cancelToken: source.token,
  };

  onCancel(() => source.cancel('The user aborted a request.'));

  try {
    return await axiosClient.request(requestConfig);
  } catch (error) {
    const axiosError = error as AxiosError<T>;
    if (axiosError.response) {
      return axiosError.response;
    }
    throw error;
  }
}

export const getResponseHeader = (
  response: AxiosResponse<any>,
  responseHeader?: string
): string | undefined => {
  if (responseHeader) {
    const content = response.headers[responseHeader];
    if (isString(content)) {
      return content;
    }
  }
  return undefined;
};

export const getResponseBody = (response: AxiosResponse<any>): any => {
  if (response.status !== 204) {
    return response.data;
  }
  return undefined;
};

export const catchErrorCodes = (options: ApiRequestOptions, result: ApiResult): void => {
  const errors: Record<number, string> = {
    400: 'Bad Request',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    ...options.errors,
  };

  // Skip throwing errors for auth issues - let the interceptor handle them
  if (result.status === 401 || result.status === 403) {
    const isAuthenticationError =
      result.body?.detail === 'Authentication credentials were not provided.' ||
      result.body?.detail === 'Invalid token.' ||
      result.body?.detail === 'Session has expired.' ||
      result.body?.error === 'Permission denied.' ||
      result.status === 401;

    if (isAuthenticationError) {
      // Still throw the error so mutations fail properly
      // The interceptor will handle the redirect separately
      throw new ApiError(options, result, 'Authentication required');
    }
  }

  const error = errors[result.status];
  if (error) {
    throw new ApiError(options, result, error);
  }

  if (!result.ok) {
    const errorStatus = result.status ?? 'unknown';
    const errorStatusText = result.statusText ?? 'unknown';
    const errorBody = (() => {
      try {
        return JSON.stringify(result.body, null, 2);
      } catch (e) {
        return undefined;
      }
    })();

    throw new ApiError(
      options,
      result,
      `Generic Error: status: ${errorStatus}; status text: ${errorStatusText}; body: ${errorBody}`
    );
  }
};

function getAxiosClient() {
  // If we need to differentiate between public and private API at some point in the future,
  // place logic here to determine which client to use.  This was used when jwts were implemented,
  // but complexity is being removed until needed again.
  return api;
}

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @param axiosClient The axios client instance to use
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export function request<T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  axiosClient?: AxiosInstance
): CancelablePromise<T> {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    try {
      const url = getUrl(config, options);
      const formData = getFormData(options);
      const body = getRequestBody(options);
      const headers = await getHeaders(config, options, formData);

      if (!onCancel.isCancelled) {
        const response = await sendRequest<T>(
          config,
          options,
          url,
          body,
          formData,
          headers,
          onCancel,
          axiosClient || getAxiosClient()
        );
        const responseBody = getResponseBody(response);
        const responseHeader = getResponseHeader(response, options.responseHeader);

        const result: ApiResult = {
          url,
          ok: isSuccess(response.status),
          status: response.status,
          statusText: response.statusText,
          body: responseHeader ?? responseBody,
        };

        catchErrorCodes(options, result);

        if (options.responseHeader) {
          resolve(result.body);
        } else {
          // If we want the full result (including status), we could return it here
          // but that would break all existing calls.
          // Instead, let's attach the status to the body if it's an object
          if (
            typeof result.body === 'object' &&
            result.body !== null &&
            !Array.isArray(result.body)
          ) {
            Object.defineProperty(result.body, '$responseStatus', {
              value: result.status,
              enumerable: false,
              writable: false,
              configurable: true,
            });
          }
          resolve(result.body);
        }
      }
    } catch (error) {
      reject(error);
    }
  });
}
